import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AgentRefreshService } from '../../agent/agent-refresh.service';
import { CourseStructureService } from '../../services/course-structure.service';
import { BLOOM, buildRegisterView, DQR, GoalView, parseGoalsCsv, RegisterView } from './objective-register.model';

/**
 * Lernziel-Register of one course (PRD mastery-objectives M2/M3), shown in
 * the course-structure dialog: the customer's goals (curriculum, import,
 * own) with the course objectives they are made of — suggested by vector
 * search, confirmed or rejected by the lecturer — and the course's learning
 * objectives (from the material or added by hand) with their DQR competence.
 * Mastery of a goal is derived from its confirmed links (competency profile,
 * view "Ziele des Kurses").
 */
@Component({
  selector: 'app-objective-register',
  templateUrl: './objective-register.component.html',
  styleUrl: './objective-register.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectiveRegisterComponent implements OnChanges {
  @Input() courseId: string | null = null;
  @Input() chapters: { id: string; title: string }[] = [];

  readonly I18N = 'components.objectiveRegister';
  readonly bloomLevels = BLOOM;
  readonly dqrAreas = DQR;
  loading = false;
  view: RegisterView = { goals: [], objectives: [], proposedTotal: 0 };
  busy: string | null = null;
  adding: 'goal' | 'objective' | null = null;
  draft = { description: '', bloom_level: '', dqr_area: '', chapter_id: '' };
  linkPick: Record<string, string> = {};

  constructor(
    private svc: CourseStructureService,
    private translate: TranslateService,
    private toastr: NbToastrService,
    private cdr: ChangeDetectorRef,
    agentRefresh: AgentRefreshService,
  ) {
    // The in-house agent changed an objective or a goal link: reload.
    agentRefresh.on('courses').pipe(takeUntilDestroyed()).subscribe(() => this.load());
  }

  ngOnChanges(): void {
    this.load();
  }

  load(): void {
    if (!this.courseId) return;
    this.loading = !this.view.goals.length && !this.view.objectives.length;
    this.svc.getRegister(this.courseId).pipe(finalize(() => {
      this.loading = false;
      this.cdr.markForCheck();
    })).subscribe({
      next: (reg) => (this.view = buildRegisterView(reg)),
      error: () => this.fail(),
    });
  }

  startAdd(kind: 'goal' | 'objective'): void {
    this.adding = kind;
    this.draft = { description: '', bloom_level: '', dqr_area: '', chapter_id: '' };
  }

  saveDraft(): void {
    if (!this.courseId || !this.draft.description.trim()) return;
    const body = {
      description: this.draft.description.trim(),
      bloom_level: this.draft.bloom_level || null,
      dqr_area: this.adding === 'objective' ? this.draft.dqr_area || null : null,
      chapter_id: this.adding === 'objective' ? this.draft.chapter_id || null : null,
      source: this.adding === 'goal' ? ('client' as const) : ('lecturer' as const),
    };
    this.run('add', this.svc.createObjective(this.courseId, body), () => (this.adding = null));
  }

  setDqr(key: string, area: string): void {
    if (!this.courseId) return;
    this.run(`dqr:${key}`, this.svc.updateObjective(this.courseId, key, { dqr_area: area || null }));
  }

  setActive(key: string, active: boolean): void {
    if (!this.courseId) return;
    this.run(`active:${key}`, this.svc.updateObjective(this.courseId, key, { active }));
  }

  link(goal: GoalView, source: string, status: 'confirmed' | 'rejected'): void {
    if (!this.courseId || !source) return;
    this.run(`link:${goal.key}:${source}`, this.svc.setGoalLink(this.courseId, goal.key, source, status),
      () => delete this.linkPick[goal.key]);
  }

  suggest(): void {
    if (!this.courseId) return;
    this.run('suggest', this.svc.suggestGoalLinks(this.courseId), (r: any) =>
      this.toastr.info(this.t('suggested', { count: r?.proposed ?? 0 }), this.t('title')));
  }

  importFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.courseId) return;
    file.text().then((raw) => {
      const rows = parseGoalsCsv(raw);
      if (!rows.length) {
        this.toastr.warning(this.t('importEmpty'), this.t('title'));
        return;
      }
      this.run('import', this.svc.importGoals(this.courseId!, rows), (r: any) =>
        this.toastr.success(this.t('imported', { created: r?.created ?? 0, updated: r?.updated ?? 0 }), this.t('title')));
    });
  }

  trackKey(_i: number, x: { key: string }): string {
    return x.key;
  }

  private run(tag: string, call: any, done?: (r: any) => void): void {
    this.busy = tag;
    call.pipe(finalize(() => {
      this.busy = null;
      this.cdr.markForCheck();
    })).subscribe({
      next: (r: any) => {
        done?.(r);
        this.load();
      },
      error: () => this.fail(),
    });
  }

  private fail(): void {
    this.toastr.danger(this.t('error'), this.t('title'));
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(`${this.I18N}.${key}`, params);
  }
}
